import {defer, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {Suspense} from 'react';
import {Await, useLoaderData} from '@remix-run/react';
import {ProductSwimlane, FeaturedCollections, Hero} from '~/components';
import {
  MEDIA_FRAGMENT,
  PRODUCT_CARD_FRAGMENT,
  PRODUCT_FRAGMENT,
} from '~/data/fragments';
import {getHeroPlaceholder} from '~/lib/placeholders';
import {
  canonicalOrigin,
  canonicalUrl,
  isNoIndexUrl,
  titleTemplateFor,
} from '~/lib/seo.server';
import {Link} from '~/components/Link';
import {legacyRedirectTarget, salvageLegacySlug} from '~/lib/legacyRedirects';
import type {
  Collection,
  CollectionConnection,
  Metafield,
  ProductConnection,
} from '@shopify/hydrogen/storefront-api-types';
import {AnalyticsPageType} from '@shopify/hydrogen';
import {routeHeaders, CACHE_SHORT, CACHE_LONG} from '~/data/cache';
import type {LinksFunction} from '@shopify/remix-oxygen';

//components
import React from 'react';
import Module from 'components/modules/Module';
import PreorderProducts from 'components/modules/PreorderProducts';
import ServiceBanners from 'components/modules/ServiceBanners';
import HomeReviews from 'components/modules/HomeReviews';
import NewArrivals from 'components/modules/NewArrivals';
import RecommendedBrands from 'components/modules/RecommendedBrands';

//domains
import {
  useHomeQuery as fetchHomeData,
  useRecommendedBrandsQuery as fetchRecommendedBrands,
} from 'domains/home';
import {getCollections} from 'domains/collections';

import {
  getCountryCodeForSanityQuery,
  getLangagueCodeForSanityQuery,
  isTWSite,
} from '~/domains/sanity';
import type {HomeSanityData} from 'domains/home';
import type {
  SanityModule,
  SanityModuleHeroBanner,
  SanityModuleSelectedCollections,
  SanityModuleSelectedProducts,
} from 'domains/modules';
import HeroBannerCarousel from 'components/modules/HeroBannerCarousel';
import HomePromoPopup from 'components/global/HomePromoPopup';

interface HomeSeoData {
  shop: {
    name: string;
    description: string;
  };
}

export interface CollectionHero {
  byline: Metafield;
  cta: Metafield;
  handle: string;
  heading: Metafield;
  height?: 'full';
  loading?: 'eager' | 'lazy';
  spread: Metafield;
  spreadSecondary: Metafield;
  top?: boolean;
}

const scripts: any = () => {
  // return [
  //   {
  //     src: 'https://loox.io/widget/4yxToKYeUo/loox.1635228300187.js?shop=helmetking-0001.myshopify.com',
  //     crossOrigin: 'anonymous',
  //     async: 'true',
  //   },
  // ];
};

export const handle = {scripts};

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export const headers = routeHeaders;

export async function loader({params, context, request}: LoaderArgs) {
  const {language, country} = context.storefront.i18n;
  // Validate the URL lang prefix against the resolved locale's PATH PREFIX,
  // not the Shopify language code. /zh-cn intentionally runs on language
  // ZH_TW, so comparing params.lang ("zh-cn") against `language` ("zh_tw")
  // wrongly 404s the /zh-cn homepage (subpages have no such guard). An
  // unknown prefix resolves to the default locale (empty pathPrefix), so it
  // still 404s here.
  const localePrefix = context.storefront.i18n.pathPrefix.replace(/^\//, '');
  if (params.lang && params.lang.toLowerCase() !== localePrefix.toLowerCase()) {
    // A one-segment path matches this route with that segment captured as
    // `lang`, so it never reaches the catch-all. Retired single-segment URLs
    // (/collections, /policies) have to be resolved here or they 404.
    const url = new URL(request.url);
    const target = legacyRedirectTarget(url.pathname);
    if (target) {
      throw redirect(`${target}${url.search}`, 301);
    }
    // OpenCart-era root slugs (/<slug>) are captured here as `lang`, never
    // reaching the catch-all — salvage live handles before giving up.
    const salvaged = await salvageLegacySlug(context.storefront, url.pathname);
    if (salvaged) {
      throw redirect(salvaged, 301);
    }
    throw new Response(null, {status: 404});
  }

  // 預訂產品 marquee on the homepage mirrors /collections/preorder-products —
  // same Shopify collection, localized title included.
  // This and New Arrivals need nothing from Sanity, so both start now and run
  // alongside the Sanity fetch instead of after it.
  const preorderCollection = context.storefront
    .query<{
      collection: {
        title: string;
        products: {nodes: ProductConnection['nodes']};
      } | null;
    }>(
      `#graphql
      ${PRODUCT_FRAGMENT}
      query homePreorderProducts($country: CountryCode, $language: LanguageCode)
      @inContext(country: $country, language: $language) {
        collection(handle: "preorder-products") {
          title
          products(first: 10) {
            nodes {
              ...ProductMain
            }
          }
        }
      }
    `,
      {
        variables: {
          country: isTWSite(request.url) ? 'TW' : 'HK',
          language,
        },
      },
    )
    .then((d) => d?.collection)
    .catch((e) => {
      console.log(e);
      return null;
    });

  // 新到產品 / New Arrivals: the 10 most recently created products, deferred
  // so it never blocks first paint (rendered below the curated 店長推薦 row).
  // Bots wait for deferred data too (entry.server), so it starts here.
  const newArrivals = context.storefront
    .query<{products: {nodes: ProductConnection['nodes']}}>(
      `#graphql
      ${PRODUCT_FRAGMENT}
      query homeNewArrivals($country: CountryCode, $language: LanguageCode)
      @inContext(country: $country, language: $language) {
        products(first: 10, sortKey: CREATED_AT, reverse: true) {
          nodes {
            ...ProductMain
          }
        }
      }
    `,
      {
        variables: {
          country: isTWSite(request.url) ? 'TW' : 'HK',
          language,
        },
      },
    )
    .then((d) => d?.products?.nodes ?? null)
    .catch(() => null);

  // Fetch the curated brand row in parallel with the home document so it adds
  // no serial latency to the (already slowest) homepage loader.
  const [homeData, recommendedBrands] = await Promise.all([
    fetchHomeData({
      languageCode: getLangagueCodeForSanityQuery(language),
      countryCode: isTWSite(request.url) ? 'zh-tw' : 'zh-hk',
    }),
    fetchRecommendedBrands(),
  ]);
  // if (!cache.has('homeSanityData')) {
  const homeSanityData = homeData as HomeSanityData;
  //   await cache.set('homeSanityData', homeSanityData);
  // } else {
  //   homeSanityData = await cache.get('homeSanityData');
  // }

  const featuredProductsSection = homeSanityData?.modules.find(
    (m) => m._type === 'module.selectedShopifyProducts',
  ) as SanityModuleSelectedProducts;
  const featuredCollectionsSection = homeSanityData?.modules.find(
    (m) => m._type === 'custom.selectShopifyCollections',
  ) as SanityModuleSelectedCollections;
  const featuredProductIds =
    featuredProductsSection &&
    featuredProductsSection._type === 'module.selectedShopifyProducts' &&
    Array.isArray(featuredProductsSection?.modules) &&
    featuredProductsSection?.modules
      ?.map((m) => (m.product?.store?.gid as string) ?? null)
      .filter((id) => id !== 'undefined' && id !== null);

  const featuredCollectionIds =
    featuredCollectionsSection &&
    featuredCollectionsSection._type === 'custom.selectShopifyCollections' &&
    Array.isArray(featuredCollectionsSection.shopify_collections) &&
    featuredCollectionsSection?.shopify_collections
      ?.map((c) => c.shopify_collection?.store?.gid ?? null)
      .filter((id) => id !== 'undefined' && id !== null);

  // const {shop, hero} = await context.storefront.query<{
  //   hero: CollectionHero;
  //   shop: HomeSeoData;
  // }>(HOMEPAGE_SEO_QUERY, {
  //   variables: {handle: 'freestyle'},
  // });

  // Featured products and collections need the Sanity picks; none of the
  // three needs another, so they run together rather than one after another.
  const [featuredProducts, featuredCollections, preorder] = await Promise.all([
    featuredProductIds
      ? context.storefront
          .query<{
            products: ProductConnection['nodes'];
          }>(
            `#graphql
      ${PRODUCT_FRAGMENT}
      query homeFeaturedProducts($country: CountryCode, $language: LanguageCode, $gids: [ID!]!)
      @inContext(country: $country, language: $language) {
        products: nodes(ids: $gids) {
          ...on Product{
            ...ProductMain
          }
        }

      }
    `,
            {
              variables: {
                country: isTWSite(request.url) ? 'TW' : 'HK',
                language,
                gids: featuredProductIds ?? [''],
              },
            },
          )
          .then((d) => d.products)
          .catch((e) => {
            console.log(e);
            return undefined;
          })
      : undefined,
    // Previously kept in a module-level LRU under one key for every
    // language, so /en could be served the Chinese titles; anonymous
    // visitors never hit it anyway (no previous-lang cookie).
    featuredCollectionIds
      ? getCollections({collectionIds: featuredCollectionIds, context}).then(
          ([collections]) => collections,
        )
      : undefined,
    preorderCollection,
  ]);
  const preorderProducts = preorder?.products?.nodes;
  const preorderCollectionTitle = preorder?.title;

  const isEnglish = language === 'EN';
  const homeTitle =
    homeSanityData?.seo?.title?.trim() ||
    (isEnglish
      ? 'Motorcycle Helmets & Riding Gear in Hong Kong'
      : '香港頭盔、電單車用品及騎士裝備');
  const homeDescription =
    homeSanityData?.seo?.description?.trim() ||
    (isEnglish
      ? 'Shop motorcycle helmets, riding gear, parts and accessories at Helmetking. Find products online or visit our Mong Kok store in Hong Kong.'
      : '頭盔王提供電單車頭盔、騎士裝備、零件及配件。網上選購產品，或親臨香港旺角門市了解產品及尺碼。');
  const homeUrl = canonicalUrl(request.url);

  return defer(
    {
      home: homeSanityData,
      isEnglish,
      featuredProducts,
      recommendedBrands,
      newArrivals,
      preorderProducts,
      preorderCollectionTitle,
      featuredCollections,
      analytics: {
        pageType: AnalyticsPageType.home,
      },
      // seo,
      seo: {
        title: homeTitle,
        titleTemplate: titleTemplateFor(homeTitle),
        description: homeDescription,
        url: homeUrl,
        robots: {
          noIndex: isNoIndexUrl(request.url),
          noFollow: false,
        },
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${homeUrl}#webpage`,
          url: homeUrl,
          name: homeTitle,
          description: homeDescription,
          inLanguage: isEnglish ? `en-${country}` : `zh-Hant-${country}`,
          isPartOf: {'@id': `${canonicalOrigin(request.url)}/#website`},
        },
      },
      // shop,
      // primaryHero: hero,
      // These different queries are separated to illustrate how 3rd party content
      // fetching can be optimized for both above and below the fold.
      // featuredProducts: context.storefront.query<{
      //   products: ProductConnection;
      // }>(HOMEPAGE_FEATURED_PRODUCTS_QUERY, {
      //   variables: {
      //     /**
      //      * Country and language properties are automatically injected
      //      * into all queries. Passing them is unnecessary unless you
      //      * want to override them from the following default:
      //      */
      //     country,
      //     language,
      //   },
      // }),
      // secondaryHero: context.storefront.query<{hero: CollectionHero}>(
      //   COLLECTION_HERO_QUERY,
      //   {
      //     variables: {
      //       handle: 'backcountry',
      //       country,
      //       language,
      //     },
      //   },
      // ),
      // featuredCollections: context.storefront.query<{
      //   collections: CollectionConnection;
      // }>(FEATURED_COLLECTIONS_QUERY, {
      //   variables: {
      //     country,
      //     language,
      //   },
      // }),
      // tertiaryHero: context.storefront.query<{hero: CollectionHero}>(
      //   COLLECTION_HERO_QUERY,
      //   {
      //     variables: {
      //       handle: 'winter-2022',
      //       country,
      //       language,
      //     },
      //   },
      // ),
    },
    {
      headers: {
        'Cache-Control': CACHE_SHORT,
        // 'Cache-Control': CACHE_LONG,
      },
    },
  );
}

type HeroBannerGroup = {
  _type: 'hero_banner_group';
  _key: string;
  banners: SanityModuleHeroBanner[];
};

/**
 * Group consecutive hero banner modules so editors can turn the top banner
 * into a carousel just by adding more hero banner modules in Sanity Studio.
 * A run of one renders exactly as before; two or more become a carousel.
 */
function groupHeroBanners(
  modules: SanityModule[],
): (SanityModule | HeroBannerGroup)[] {
  const grouped: (SanityModule | HeroBannerGroup)[] = [];
  for (const module of modules) {
    const last = grouped[grouped.length - 1];
    if (module._type === 'module.hero_banner') {
      if (last && last._type === 'hero_banner_group') {
        last.banners.push(module as SanityModuleHeroBanner);
      } else {
        grouped.push({
          _type: 'hero_banner_group',
          _key: `hero-group-${module._key}`,
          banners: [module as SanityModuleHeroBanner],
        });
      }
    } else {
      grouped.push(module);
    }
  }
  return grouped;
}

export default function Homepage() {
  const {
    // primaryHero,
    // secondaryHero,
    // tertiaryHero,
    // featuredCollections,
    // featuredProducts,
    home,
    isEnglish,
  } = useLoaderData<typeof loader>();

  // TODO: skeletons vs placeholders
  const skeletons = getHeroPlaceholder([{}, {}, {}]);

  const groupedModules = groupHeroBanners(home.modules);

  return (
    <>
      {/* The homepage had no <h1>; the hero is an image. Add a semantic,
          visually-hidden H1 so the page has one clear top-level heading. */}
      <h1 className="sr-only">
        {isEnglish
          ? 'Helmetking – Motorcycle Helmets & Riding Gear in Hong Kong'
          : '頭盔王 – 香港電單車頭盔及騎士裝備'}
      </h1>
      {/* Cardo summer-sale carousel; self-expires 2026-09-07 23:59 HKT. */}
      <HomePromoPopup />
      {groupedModules.map((module) =>
        module._type === 'hero_banner_group' ? (
          module.banners.length > 1 ? (
            <HeroBannerCarousel key={module._key} modules={module.banners} />
          ) : (
            <Module key={module.banners[0]._key} module={module.banners[0]} />
          )
        ) : module._type === 'module.looxReviews' ? (
          // The homepage still carries a standalone Loox module in Sanity;
          // reviews are now shown once, together with Google reviews, in the
          // HomeReviews band below the products — so skip this duplicate.
          <React.Fragment key={module._key} />
        ) : (
          <React.Fragment key={module._key}>
            {/* 推薦品牌列 renders directly above the 店長推薦
                (selectedShopifyProducts) marquee. */}
            {module._type === 'module.selectedShopifyProducts' && (
              <RecommendedBrands />
            )}
            <Module module={module} />
            {module._type === 'module.selectedShopifyProducts' && (
              <>
                <NewArrivals />
                <PreorderProducts />
                <ServiceBanners />
              </>
            )}
            {/* 顧客評價 band renders directly below 多元化網店體驗 (GridSection). */}
            {module._type === 'module.gridSection' && <HomeReviews />}
          </React.Fragment>
        ),
      )}
      <section
        className="mx-auto max-w-5xl px-6 py-10"
        aria-labelledby="about-helmetking"
      >
        <h2 id="about-helmetking" className="text-xl font-bold">
          {isEnglish ? 'Helmetking in Hong Kong' : '香港頭盔王'}
        </h2>
        <p className="mt-3 leading-relaxed">
          {isEnglish
            ? 'Helmetking sells motorcycle helmets, riding gear, parts and accessories online and at our Mong Kok store. Browse the product range, find store details, or read answers to common shopping questions.'
            : '頭盔王提供電單車頭盔、騎士裝備、零件及配件，可於網上選購或親臨旺角門市。你可以瀏覽產品、查閱門市資料，或了解購物常見問題。'}
        </p>
        <nav
          className="mt-4 flex flex-wrap gap-5 underline"
          aria-label={isEnglish ? 'Shopping information' : '購物資訊'}
        >
          <Link to="/collections/all-products">
            {isEnglish ? 'Shop products' : '選購產品'}
          </Link>
          <Link to="/store-locator">
            {isEnglish ? 'Store address and hours' : '門市地址及營業時間'}
          </Link>
          <Link to="/pages/faq">
            {isEnglish ? 'Shopping FAQ' : '購物常見問題'}
          </Link>
        </nav>
      </section>
      {/* {primaryHero && (
        <Hero {...primaryHero} height="full" top loading="eager" />
      )}

      {featuredProducts && (
        <Suspense>
          <Await resolve={featuredProducts}>
            {({products}) => {
              if (!products?.nodes) return <></>;
              return (
                <ProductSwimlane
                  products={products.nodes}
                  title="Featured Products"
                  count={4}
                />
              );
            }}
          </Await>
        </Suspense>
      )}

      {secondaryHero && (
        <Suspense fallback={<Hero {...skeletons[1]} />}>
          <Await resolve={secondaryHero}>
            {({hero}) => {
              if (!hero) return <></>;
              return <Hero {...hero} />;
            }}
          </Await>
        </Suspense>
      )}

      {featuredCollections && (
        <Suspense>
          <Await resolve={featuredCollections}>
            {({collections}) => {
              if (!collections?.nodes) return <></>;
              return (
                <FeaturedCollections
                  collections={collections.nodes}
                  title="Collections"
                />
              );
            }}
          </Await>
        </Suspense>
      )}

      {tertiaryHero && (
        <Suspense fallback={<Hero {...skeletons[2]} />}>
          <Await resolve={tertiaryHero}>
            {({hero}) => {
              if (!hero) return <></>;
              return <Hero {...hero} />;
            }}
          </Await>
        </Suspense>
      )} */}
    </>
  );
}

const COLLECTION_CONTENT_FRAGMENT = `#graphql
  ${MEDIA_FRAGMENT}
  fragment CollectionContent on Collection {
    id
    handle
    title
    descriptionHtml
    heading: metafield(namespace: "hero", key: "title") {
      value
    }
    byline: metafield(namespace: "hero", key: "byline") {
      value
    }
    cta: metafield(namespace: "hero", key: "cta") {
      value
    }
    spread: metafield(namespace: "hero", key: "spread") {
      reference {
        ...Media
      }
    }
    spreadSecondary: metafield(namespace: "hero", key: "spread_secondary") {
      reference {
        ...Media
      }
    }
  }
`;

const HOMEPAGE_SEO_QUERY = `#graphql
  ${COLLECTION_CONTENT_FRAGMENT}
  query collectionContent($handle: String, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    hero: collection(handle: $handle) {
      ...CollectionContent
    }
    shop {
      name
      description
    }
  }
`;

const COLLECTION_HERO_QUERY = `#graphql
  ${COLLECTION_CONTENT_FRAGMENT}
  query collectionContent($handle: String, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    hero: collection(handle: $handle) {
      ...CollectionContent
    }
  }
`;

// @see: https://shopify.dev/api/storefront/latest/queries/products
export const HOMEPAGE_FEATURED_PRODUCTS_QUERY = `#graphql
  ${PRODUCT_CARD_FRAGMENT}
  query homepageFeaturedProducts($country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    products(first: 8) {
      nodes {
        ...ProductCard
      }
    }
  }
`;

// @see: https://shopify.dev/api/storefront/latest/queries/collections
export const FEATURED_COLLECTIONS_QUERY = `#graphql
  query homepageFeaturedCollections($country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    collections(
      first: 4,
      sortKey: UPDATED_AT
    ) {
      nodes {
        id
        title
        handle
        image {
          altText
          width
          height
          url
        }
      }
    }
  }
`;
